import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { 
  Printer, Download, FileSpreadsheet, X, Search, 
  ChevronDown, ChevronRight, Package, Users, Calendar,
  IndianRupee, FileText, ExternalLink
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to format date (remove time)
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // Handle various date formats and remove time
  const date = dateStr.split(' ')[0].split('T')[0];
  return date || '-';
};

// Standalone Report Page Component (opens in new tab)
const SupplierOrdersReportPage = ({ orders, stockData = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [filterSupplier, setFilterSupplier] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  // Create a map of product costs from stock data
  const productCosts = useMemo(() => {
    const costs = {};
    if (stockData && stockData.length > 0) {
      stockData.forEach(item => {
        if (item.product_name && item.cost) {
          costs[item.product_name.toLowerCase().trim()] = item.cost;
        }
      });
    }
    return costs;
  }, [stockData]);

  // Group orders by supplier (order_given_to)
  const supplierGroupedOrders = useMemo(() => {
    const filtered = orders.filter(order => {
      const matchesSearch = !searchTerm || 
        order.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.order_given_to?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSupplier = !filterSupplier || order.order_given_to === filterSupplier;
      return matchesSearch && matchesSupplier;
    });

    const grouped = {};
    filtered.forEach(order => {
      const supplier = order.order_given_to || 'Unknown Supplier';
      if (!grouped[supplier]) {
        grouped[supplier] = {
          supplier,
          orders: [],
          totalQty: 0,
          totalValue: 0,
          orderCount: 0
        };
      }
      
      // Get cost from stock data if available
      const productKey = order.product_name?.toLowerCase().trim();
      const cost = productCosts[productKey] || order.cost || order.rate || 0;
      const orderValue = (order.order_qty || 0) * cost;
      
      grouped[supplier].orders.push({
        ...order,
        cost: cost,
        order_value: orderValue
      });
      grouped[supplier].totalQty += order.order_qty || 0;
      grouped[supplier].totalValue += orderValue;
      grouped[supplier].orderCount += 1;
    });

    return Object.values(grouped).sort((a, b) => a.supplier.localeCompare(b.supplier));
  }, [orders, searchTerm, filterSupplier, productCosts]);

  // Get unique suppliers for filter dropdown
  const uniqueSuppliers = useMemo(() => {
    const suppliers = [...new Set(orders.map(o => o.order_given_to).filter(Boolean))];
    return suppliers.sort();
  }, [orders]);

  // Calculate totals
  const totals = useMemo(() => {
    return supplierGroupedOrders.reduce((acc, group) => ({
      totalQty: acc.totalQty + group.totalQty,
      totalValue: acc.totalValue + group.totalValue,
      totalOrders: acc.totalOrders + group.orderCount
    }), { totalQty: 0, totalValue: 0, totalOrders: 0 });
  }, [supplierGroupedOrders]);

  const toggleSupplier = (supplier) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplier]: !prev[supplier]
    }));
  };

  const expandAll = () => {
    const all = {};
    supplierGroupedOrders.forEach(g => { all[g.supplier] = true; });
    setExpandedSuppliers(all);
  };

  const collapseAll = () => {
    setExpandedSuppliers({});
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  // Direct PDF Export
  const generatePDF = (suppliersToExport, allInOne = true) => {
    if (allInOne) {
      // Single PDF with all suppliers
      const doc = new jsPDF();
      let yPos = 20;
      
      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('WINLIFE PHARMA - Pending Orders Report', 14, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPos);
      yPos += 6;
      doc.text(`Suppliers: ${suppliersToExport.length} | Orders: ${totals.totalOrders} | Qty: ${totals.totalQty.toLocaleString()}${totals.totalValue > 0 ? ` | Value: ₹${totals.totalValue.toLocaleString()}` : ''}`, 14, yPos);
      yPos += 12;

      suppliersToExport.forEach((group, idx) => {
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        // Supplier header
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(240, 240, 240);
        doc.rect(14, yPos - 5, 182, 8, 'F');
        doc.text(`${group.supplier} (${group.orderCount} orders)`, 16, yPos);
        yPos += 10;

        // Table headers
        const hasValue = group.totalValue > 0;
        const headers = hasValue 
          ? [['Product Name', 'Qty', 'Cost', 'Value', 'Order Date', 'Notes']]
          : [['Product Name', 'Qty', 'Order Date', 'Notes']];

        const rows = group.orders.map(order => {
          const baseRow = [
            order.product_name || '-',
            (order.order_qty || 0).toLocaleString(),
          ];
          if (hasValue) {
            baseRow.push(
              order.cost ? `₹${order.cost.toLocaleString()}` : '-',
              order.order_value ? `₹${order.order_value.toLocaleString()}` : '-'
            );
          }
          baseRow.push(
            formatDate(order.order_given_date),
            order.notes || '-'
          );
          return baseRow;
        });

        // Subtotal row
        const subtotalRow = hasValue
          ? ['Subtotal', group.totalQty.toLocaleString(), '', `₹${group.totalValue.toLocaleString()}`, `${group.orderCount} orders`, '']
          : ['Subtotal', group.totalQty.toLocaleString(), `${group.orderCount} orders`, ''];
        rows.push(subtotalRow);

        autoTable(doc, {
          startY: yPos,
          head: headers,
          body: rows,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [66, 139, 202], textColor: 255 },
          columnStyles: hasValue ? {
            0: { cellWidth: 50 },
            1: { cellWidth: 20, halign: 'right' },
            2: { cellWidth: 25, halign: 'right' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 30 },
            5: { cellWidth: 32 }
          } : {
            0: { cellWidth: 70 },
            1: { cellWidth: 30, halign: 'right' },
            2: { cellWidth: 40 },
            3: { cellWidth: 42 }
          },
          didParseCell: function(data) {
            if (data.row.index === rows.length - 1) {
              data.cell.styles.fillColor = [200, 230, 201];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        yPos = doc.lastAutoTable.finalY + 10;
      });

      doc.save(`Pending_Orders_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } else {
      // Separate PDFs for each supplier
      suppliersToExport.forEach(group => {
        const doc = new jsPDF();
        let yPos = 20;
        
        // Header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`WINLIFE PHARMA - Orders for ${group.supplier}`, 14, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPos);
        yPos += 6;
        doc.text(`Orders: ${group.orderCount} | Total Qty: ${group.totalQty.toLocaleString()}${group.totalValue > 0 ? ` | Value: ₹${group.totalValue.toLocaleString()}` : ''}`, 14, yPos);
        yPos += 12;

        const hasValue = group.totalValue > 0;
        const headers = hasValue 
          ? [['Product Name', 'Qty', 'Cost', 'Value', 'Order Date', 'Notes']]
          : [['Product Name', 'Qty', 'Order Date', 'Notes']];

        const rows = group.orders.map(order => {
          const baseRow = [
            order.product_name || '-',
            (order.order_qty || 0).toLocaleString(),
          ];
          if (hasValue) {
            baseRow.push(
              order.cost ? `₹${order.cost.toLocaleString()}` : '-',
              order.order_value ? `₹${order.order_value.toLocaleString()}` : '-'
            );
          }
          baseRow.push(
            formatDate(order.order_given_date),
            order.notes || '-'
          );
          return baseRow;
        });

        // Subtotal
        const subtotalRow = hasValue
          ? ['TOTAL', group.totalQty.toLocaleString(), '', `₹${group.totalValue.toLocaleString()}`, '', '']
          : ['TOTAL', group.totalQty.toLocaleString(), '', ''];
        rows.push(subtotalRow);

        autoTable(doc, {
          startY: yPos,
          head: headers,
          body: rows,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [66, 139, 202], textColor: 255 },
          didParseCell: function(data) {
            if (data.row.index === rows.length - 1) {
              data.cell.styles.fillColor = [200, 230, 201];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        // Clean filename
        const safeSupplier = group.supplier.replace(/[^a-zA-Z0-9]/g, '_');
        doc.save(`Orders_${safeSupplier}_${new Date().toISOString().split('T')[0]}.pdf`);
      });
    }
  };

  // Export to Excel
  const generateExcel = (suppliersToExport, allInOne = true) => {
    if (allInOne) {
      let csvContent = "Supplier,Product Name,Quantity,Cost,Value,Order Date,Notes\n";
      
      suppliersToExport.forEach(group => {
        group.orders.forEach(order => {
          csvContent += `"${group.supplier}","${order.product_name || ''}",${order.order_qty || 0},${order.cost || ''},${order.order_value || ''},"${formatDate(order.order_given_date)}","${(order.notes || '').replace(/"/g, '""')}"\n`;
        });
        csvContent += `"${group.supplier} - SUBTOTAL","",${group.totalQty},,${group.totalValue},"","${group.orderCount} orders"\n`;
      });
      
      csvContent += `"GRAND TOTAL","",${totals.totalQty},,${totals.totalValue},"","${totals.totalOrders} orders"\n`;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Pending_Orders_Report_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      // Separate files for each supplier
      suppliersToExport.forEach(group => {
        let csvContent = "Product Name,Quantity,Cost,Value,Order Date,Notes\n";
        
        group.orders.forEach(order => {
          csvContent += `"${order.product_name || ''}",${order.order_qty || 0},${order.cost || ''},${order.order_value || ''},"${formatDate(order.order_given_date)}","${(order.notes || '').replace(/"/g, '""')}"\n`;
        });
        csvContent += `"TOTAL",${group.totalQty},,${group.totalValue},"",""\n`;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeSupplier = group.supplier.replace(/[^a-zA-Z0-9]/g, '_');
        link.download = `Orders_${safeSupplier}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      });
    }
  };

  // Export Modal
  const ExportModal = ({ type, onClose }) => {
    const handleExport = (allInOne) => {
      if (type === 'pdf') {
        generatePDF(supplierGroupedOrders, allInOne);
      } else {
        generateExcel(supplierGroupedOrders, allInOne);
      }
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            {type === 'pdf' ? <FileText className="w-5 h-5 text-red-600" /> : <FileSpreadsheet className="w-5 h-5 text-green-600" />}
            Export {type.toUpperCase()} Report
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            How would you like to export the report for {supplierGroupedOrders.length} suppliers?
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => handleExport(true)} 
              className="w-full bg-blue-600 hover:bg-blue-700 justify-start"
            >
              <Package className="w-4 h-4 mr-2" />
              All Suppliers in One {type.toUpperCase()}
            </Button>
            <Button 
              onClick={() => handleExport(false)} 
              variant="outline"
              className="w-full justify-start"
            >
              <Users className="w-4 h-4 mr-2" />
              Separate {type.toUpperCase()} for Each Supplier
            </Button>
            <Button 
              onClick={onClose} 
              variant="ghost"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-content { padding: 20px; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 no-print">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6" />
              Pending Orders Report - Supplier Wise
            </h1>
            <p className="text-blue-100 text-sm mt-1">
              {totals.totalOrders} orders from {supplierGroupedOrders.length} suppliers
              {totals.totalValue > 0 && ` • Total Value: ₹${totals.totalValue.toLocaleString()}`}
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b shadow-sm no-print sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products or suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            className="h-10 px-3 border border-slate-300 rounded-md bg-white text-sm"
          >
            <option value="">All Suppliers</option>
            {uniqueSuppliers.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <Button onClick={expandAll} variant="outline" size="sm">
              Expand All
            </Button>
            <Button onClick={collapseAll} variant="outline" size="sm">
              Collapse All
            </Button>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button onClick={handlePrint} variant="outline" size="sm">
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Button 
              onClick={() => setShowExportModal('pdf')} 
              variant="outline" 
              size="sm" 
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <Download className="w-4 h-4 mr-1" />
              PDF
            </Button>
            <Button 
              onClick={() => setShowExportModal('excel')} 
              variant="outline" 
              size="sm" 
              className="text-green-600 border-green-300 hover:bg-green-50"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-6 py-4 no-print">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Suppliers</span>
            </div>
            <p className="text-2xl font-bold text-blue-900 mt-1">{supplierGroupedOrders.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-600 font-medium">Total Orders</span>
            </div>
            <p className="text-2xl font-bold text-green-900 mt-1">{totals.totalOrders}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-purple-600 font-medium">Total Quantity</span>
            </div>
            <p className="text-2xl font-bold text-purple-900 mt-1">{totals.totalQty.toLocaleString()}</p>
          </div>
          {totals.totalValue > 0 && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-600 font-medium">Total Value</span>
              </div>
              <p className="text-2xl font-bold text-amber-900 mt-1">₹{totals.totalValue.toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-6 print-content">
        {/* Print Header */}
        <div className="hidden print:block mb-6">
          <h1 className="text-2xl font-bold">WINLIFE PHARMA - Pending Orders Report</h1>
          <p className="text-sm text-gray-600">Generated: {new Date().toLocaleDateString()}</p>
          <p className="text-sm">
            Suppliers: {supplierGroupedOrders.length} | Orders: {totals.totalOrders} | Qty: {totals.totalQty.toLocaleString()}
            {totals.totalValue > 0 && ` | Value: ₹${totals.totalValue.toLocaleString()}`}
          </p>
        </div>

        {supplierGroupedOrders.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg">No pending orders found</p>
            <p className="text-sm">Adjust filters or add new orders</p>
          </div>
        ) : (
          <div className="space-y-4">
            {supplierGroupedOrders.map(group => {
              const hasValue = group.totalValue > 0;
              return (
                <Card key={group.supplier} className="overflow-hidden">
                  <div 
                    className="bg-slate-100 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-200 transition-colors no-print"
                    onClick={() => toggleSupplier(group.supplier)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedSuppliers[group.supplier] ? (
                        <ChevronDown className="w-5 h-5 text-slate-600" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-600" />
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-800">{group.supplier}</h3>
                        <p className="text-sm text-slate-500">
                          {group.orderCount} order(s) • {group.totalQty.toLocaleString()} units
                          {hasValue && ` • ₹${group.totalValue.toLocaleString()}`}
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {group.orderCount} items
                    </span>
                  </div>
                  
                  {/* Print: always show expanded */}
                  <div className={`overflow-x-auto ${expandedSuppliers[group.supplier] ? '' : 'hidden print:block'}`}>
                    {/* Print supplier header */}
                    <div className="hidden print:block bg-slate-100 px-4 py-2 font-semibold">
                      {group.supplier} ({group.orderCount} orders)
                    </div>
                    <table className="w-full">
                      <thead className="bg-slate-50 border-y">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Product Name</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Quantity</th>
                          {hasValue && (
                            <>
                              <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Cost</th>
                              <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Value</th>
                            </>
                          )}
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Order Date</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {group.orders.map((order, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-4 py-2 text-sm font-medium text-slate-800">{order.product_name}</td>
                            <td className="px-4 py-2 text-sm text-slate-700 text-right font-semibold">
                              {(order.order_qty || 0).toLocaleString()}
                            </td>
                            {hasValue && (
                              <>
                                <td className="px-4 py-2 text-sm text-slate-600 text-right">
                                  {order.cost ? `₹${order.cost.toLocaleString()}` : '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-700 text-right font-medium">
                                  {order.order_value ? `₹${order.order_value.toLocaleString()}` : '-'}
                                </td>
                              </>
                            )}
                            <td className="px-4 py-2 text-sm text-slate-600">{formatDate(order.order_given_date)}</td>
                            <td className="px-4 py-2 text-sm text-slate-500">{order.notes || '-'}</td>
                          </tr>
                        ))}
                        <tr className="bg-green-50 font-semibold">
                          <td className="px-4 py-2 text-sm text-green-800">Subtotal</td>
                          <td className="px-4 py-2 text-sm text-green-800 text-right">
                            {group.totalQty.toLocaleString()}
                          </td>
                          {hasValue && (
                            <>
                              <td className="px-4 py-2 text-sm text-green-800 text-right"></td>
                              <td className="px-4 py-2 text-sm text-green-800 text-right">
                                ₹{group.totalValue.toLocaleString()}
                              </td>
                            </>
                          )}
                          <td colSpan="2" className="px-4 py-2 text-sm text-green-700">
                            {group.orderCount} order(s)
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })}

            {/* Grand Total */}
            <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-blue-100 text-sm">Grand Total</p>
                    <p className="text-2xl font-bold">{totals.totalQty.toLocaleString()} units</p>
                  </div>
                  {totals.totalValue > 0 && (
                    <div className="text-right">
                      <p className="text-blue-100 text-sm">Total Value</p>
                      <p className="text-xl font-semibold">₹{totals.totalValue.toLocaleString()}</p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-blue-100 text-sm">From</p>
                    <p className="text-xl font-semibold">{supplierGroupedOrders.length} suppliers</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-100 text-sm">Total Orders</p>
                    <p className="text-xl font-semibold">{totals.totalOrders}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal type={showExportModal} onClose={() => setShowExportModal(false)} />
      )}
    </div>
  );
};

// Modal wrapper (for backward compatibility if needed)
const SupplierOrdersReport = ({ orders, stockData, onClose }) => {
  const handleOpenInNewTab = () => {
    // Store data in localStorage for the new tab (localStorage is shared between tabs)
    localStorage.setItem('supplierReportOrders', JSON.stringify(orders));
    localStorage.setItem('supplierReportStock', JSON.stringify(stockData || []));
    localStorage.setItem('supplierReportTimestamp', Date.now().toString());
    window.open('/supplier-report', '_blank');
    onClose();
  };

  // Auto-open in new tab
  useEffect(() => {
    handleOpenInNewTab();
  }, []);

  return null; // This component just opens the new tab
};

// Export both components
export default SupplierOrdersReport;
export { SupplierOrdersReportPage };
